import React, { useState, useCallback } from 'react';
import { map, filter, propEq, assoc } from 'ramda';
import styled, { css } from 'styled-components';
import CollapsibleChevronBlock from '~/components/CollapsibleChevronBlock';
import SelectableRowWithInput from '~/components/CollapsibleBlock/components/SelectableRowWithInput';
import JustificationContainer from '~/components/JustificationContainer';
import useCurrentBreakpoint from '~/hooks/useCurrentBreakpoint';
import { Kenmerk } from '../RealworksSelectableAccordion';
import alterById from '~/util/alterById/index';
import AnimatedCheckbox from '~/components/AnimatedCheckbox';

type Field = 'check' | 'tag';

export type Props = {
  dataTestId?: string;
  /** Group name */
  name: string;
  /** All kenmerken within this group */
  kenmerken: Array<Kenmerk>;
  selectable: boolean;
  onChange: (args: { kenmerkList: Array<Kenmerk> }) => void;
};

const KenmerkGroup: React.FC<Props> = ({
  dataTestId,
  kenmerken,
  name,
  selectable,
  onChange: onParentChange,
  ...rest
}) => {
  const currentBp = useCurrentBreakpoint();

  const checkedAmount: number = filter(
    propEq('checked', true),
    kenmerken,
  ).length;

  const isGroupChecked = checkedAmount === kenmerken.length;
  const partiallyChecked = !isGroupChecked && checkedAmount > 0;

  const [contentOpen, setContentOpen] = useState<boolean>(true);

  const onChange = useCallback(
    (id: number, value: string | boolean | null, field: Field) => {
      const updated = alterById(id, field, value, kenmerken);
      onParentChange({ kenmerkList: updated });
    },
    [kenmerken, onParentChange],
  );

  const markAllCheckboxes = useCallback(
    (value: boolean) =>
      onParentChange({ kenmerkList: map(assoc('checked', value), kenmerken) }),
    [kenmerken, onParentChange],
  );

  return (
    <CollapsibleChevronBlock
      data-testid={dataTestId}
      header={
        <AnimatedCheckbox
          label={<Label>{name}</Label>}
          name={`${name}-group-checkbox`}
          value={isGroupChecked || partiallyChecked}
          minus={partiallyChecked}
          selectable={selectable}
          onChange={() => {
            setContentOpen(true);
            markAllCheckboxes(
              partiallyChecked ? isGroupChecked : !isGroupChecked,
            );
          }}
        />
      }
      variant="primary"
      isOpen={contentOpen}
      onToggle={() => setContentOpen(!contentOpen)}
      {...rest}
    >
      {currentBp !== 'mobile' && (
        <JustificationContainer justification="space-between">
          <Small>Kenmerk</Small> <Small>Tag</Small>
        </JustificationContainer>
      )}

      {kenmerken.map(({ description, id, checked, tag }) => (
        <SelectableRowWithInput
          row={{
            description,
            id,
            checked,
            tag,
          }}
          key={`key-${id}`}
          checkboxTitle="Kenmerk"
          inputTitle="Tag"
          onChange={(value, field: Field) => onChange(id, value, field)}
          selectable={selectable}
        />
      ))}
    </CollapsibleChevronBlock>
  );
};

const Label = styled.span<{}>(
  ({ theme }) => css`
    font-weight: ${theme.fw('semiBold')};
  `,
);

const Small = styled.div<{}>`
  ${({ theme }) => css`
    font-size: ${theme.fs('s')};
    font-weight: ${theme.fw('semiBold')};
    width: 50%;
  `}
`;

export default KenmerkGroup;
